import { useRef, useState } from 'react';
import useAwayClick from '#/shared/hooks/useAwayClick';

const useManagePopperVisibility = ({ onPopperShow = () => {} }) => {
  const popperRef = useRef();
  const fieldRef = useRef();
  const inputRef = useRef();

  const [popperVisible, setPopperVisible] = useState(false);
  const [suppressShowPopper, setSuppressShowPopper] = useState(false);

  useAwayClick(popperRef, e => {
    setPopperVisible(false);

    // if an away-click is on the field its default behavior of opening
    // the popper needs to be suppressed; by the time the `click` handler
    // fires the popper has already closed because it's triggered by
    // `mousedown`, this state node provides a hook to cancel that action
    if (
      fieldRef.current?.contains(e.target) &&
      !inputRef.current?.contains(e.target)
    ) {
      setSuppressShowPopper(true);
    }
  });

  /**
   * Handler for button that opens the popper, pass a function to the
   * `onPopperShow` param of this hook to inject custom functionality
   */
  const showPopper = () => {
    if (suppressShowPopper) {
      setSuppressShowPopper(false);
    } else {
      setPopperVisible(true);
      onPopperShow();
    }
  };

  return {
    popperRef,
    /**
     * Use this as the picker field element's ref to prevent the popper from
     * re-opening when the field is clicked to close the popper
     */
    fieldRef,
    /**
     * Use this as the ref for field's input element to prevent clicks on the
     * input from suppressing the popper from opening the next time the popper
     * button or field are clicked
     */
    inputRef,
    popperVisible,
    showPopper,
    hidePopper: () => setPopperVisible(false),
    /**
     * Handler for field element of picker, pass this as the onClick handler
     * to have the popper open when the field element, and none of its child
     * elements, is clicked
     */
    onFieldClick: e => {
      if (e.currentTarget === e.target) showPopper();
    },
    /**
     * If using `react-focus-trap` to wrap the popper pass these settings to
     * its `FocusTrap` component for best results when using other elements of
     * this hook
     */
    focusTrapOptions: {
      // clicking outside the popper will close it, this setting ensures
      // the clicked element will gain focus (if able to)
      clickOutsideDeactivates: true,
    },
  };
};

export default useManagePopperVisibility;
